import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { GetObjectCommand, PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { GetSecretValueCommand, SecretsManagerClient } from '@aws-sdk/client-secrets-manager';
import { CognitoIdentityCredentials, fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { Buffer } from 'buffer';

const REGION = process.env.REACT_APP_GRAPHQL_REGION;
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID || '';
const userPoolId = process.env.REACT_APP_USER_POOL_ID;

function waitForIdToken(timeout = 5000): Promise<string> {
  return new Promise((resolve, reject) => {
    const interval = 50;
    let waited = 0;

    const check = () => {
      const token = sessionStorage.getItem('idToken');
      if (token) {
        resolve(token);
      } else if (waited >= timeout) {
        console.warn('Session expired: idToken not found in sessionStorage');
        reject(new Error('Session expired - please refresh the page and try again'));
      } else {
        waited += interval;
        setTimeout(check, interval);
      }
    };

    check();
  });
}

async function fetchCognitoCredentials() {
  try {
    const idToken = await waitForIdToken();
    const credentialsProvider = fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region: REGION }),
      identityPoolId: IDENTITY_POOL_ID,
      logins: {
        [`cognito-idp.${REGION}.amazonaws.com/${userPoolId}`]: idToken,
      },
    });
    const credentials = await credentialsProvider();
    return credentials;
  } catch (error) {
    console.warn(
      'Unable to fetch credentials - session may be expired:',
      error instanceof Error ? error.message : String(error)
    );
    return null;
  }
}

async function fetchSecret(credentials: CognitoIdentityCredentials, secretKey: string) {
  try {
    const secretsClient = new SecretsManagerClient({
      region: REGION,
      credentials,
    });
    const command = new GetSecretValueCommand({ SecretId: process.env.REACT_APP_AWS_SECRET_NAME });
    const response = await secretsClient.send(command);

    if (response.SecretString) {
      const secret = JSON.parse(response.SecretString)[secretKey];
      return secret;
    }
  } catch (error) {
    console.error('Error fetching secret:', error);
  }
}

function getBucketAndKey(url: string) {
  const s = url.split('/');
  const [bucket, downloadKey] = [s[2], s.splice(3).join('/')];
  return { bucket, downloadKey };
}

export async function downloadAssetFromS3(url: string, filename: string) {
  const credentials = await fetchCognitoCredentials();
  if (!credentials) {
    throw new Error('Unable to authenticate - session may be expired');
  }

  try {
    const { bucket, downloadKey } = getBucketAndKey(url);
    const s3Client = new S3Client({ region: REGION, credentials: credentials });
    const command = new GetObjectCommand({ Bucket: bucket, Key: downloadKey });
    const response = await s3Client.send(command);
    const blob = await response.Body?.transformToByteArray();
    if (blob) {
      const blobURL = URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = blobURL;
      link.download = filename + '.xlsx';
      link.click();
      URL.revokeObjectURL(blobURL);
    }
  } catch (error) {
    console.error('Error fetching resource:', error);
    throw error;
  }
}

export async function uploadAssetToS3(url: string, file: File) {
  const credentials = await fetchCognitoCredentials();
  if (!credentials) {
    throw new Error('Unable to authenticate - session may be expired');
  }

  try {
    const { bucket, downloadKey } = getBucketAndKey(url);
    const s3Client = new S3Client({ region: REGION, credentials: credentials });
    const arrayBuffer = await file.arrayBuffer();
    const command = new PutObjectCommand({
      Bucket: bucket,
      Key: downloadKey,
      Body: Buffer.from(arrayBuffer),
      ContentType: file.type,
    });
    await s3Client.send(command);
    console.log('File uploaded to S3 successfully');
  } catch (error) {
    console.error('Error uploading resource:', error);
    throw error;
  }
}

export async function getDatadogClientToken() {
  const credentials = await fetchCognitoCredentials();
  if (credentials) {
    const token = await fetchSecret(credentials, 'DATADOG_TOKEN');
    return token;
  } else {
    throw Error('Could not fetch Cognito credentials to get Datadog client token!');
  }
}
